import { useState } from "react";
import {
    Box, Button, CircularProgress, FormControlLabel,
    MenuItem, Stack, Switch, TextField, Typography,
} from "@mui/material";
import { useFieldSetter } from "src/utils/useFieldSetter";
import { buildInitialFromSchema, validateSchema } from "src/utils/hallinta/entitySchema";
import { captureError } from "src/utils/errorReporting";
import { useT } from "src/i18n";
import { parseFinnishAddress } from "src/utils/googlePlaces";
import AddressAutocomplete from "src/components/AddressAutocomplete";
import GlassDialog from "src/components/common/GlassDialog";

// Google Places address picker for schema field `type: "address"`. On pick it
// fans the chosen place out across several form keys declared on the field:
//   key          → street ("Mannerheimintie 12")    (required, validated as text)
//   postalCityKey → postal + city ("00100 Helsinki")
//   latKey/lngKey → GPS coords (persisted by the caller via updateLatLng)
//   placeIdKey    → Google placeId
// Free typing (no pick) keeps the street text but clears stale coords so the
// caller won't persist coordinates that no longer match the typed address.
function AddressField({ field, form, setForm, errors, update }) {
    const streetVal = form[field.key] ?? "";

    const clearCoords = (prev) => ({
        ...prev,
        [field.latKey]: null,
        [field.lngKey]: null,
        [field.placeIdKey]: null,
    });

    // Called for both free-typing and autocomplete-clear; clears stale coords
    // so the caller won't persist coordinates that no longer match the typed address.
    function handleValueChange(v) {
        update(field.key)({ target: { value: v } }); // sets street + clears field error
        setForm(clearCoords);
    }

    async function handlePick({ lat, lng, addressComponents, placeId, description }) {
        const { street, postalCity } = parseFinnishAddress(addressComponents);
        setForm((prev) => ({
            ...prev,
            [field.key]: street || description,
            [field.postalCityKey]: postalCity || prev[field.postalCityKey] || "",
            [field.latKey]: lat ?? null,
            [field.lngKey]: lng ?? null,
            [field.placeIdKey]: placeId,
        }));
    }

    return (
        <AddressAutocomplete
            value={streetVal}
            onValueChange={handleValueChange}
            onPick={handlePick}
            label={field.label}
            required={!!field.required}
            error={!!errors[field.key]}
            helperText={errors[field.key] || " "}
            captureErrorTags={{ entity: "sijainti", operation: "fetchPlaceDetails" }}
        />
    );
}

// Schema-driven glass dialog for create / edit operations in /hallinta.
//
// Props:
//   open        — boolean
//   onClose     — () => void
//   title       — Finnish string shown as the Black Ops One header
//   schema      — array of field descriptors (see entitySchema.js)
//   row         — existing row for edit, or null/undefined for create
//   onSubmit    — async (values) => void  (must throw on failure)
//   entityTag   — string tag sent to Sentry for diagnostics (e.g. "person")
//   validateForm — optional (form) => string|null  form-level guard run after the
//                 per-field schema validation; a non-empty string blocks submit and
//                 is shown as a form-level error (e.g. "fill at least one field").
//   children    — optional extra content rendered under the schema fields (e.g. a
//                 person's global-visibility status + action); kept generic so no
//                 entity-specific logic lives here.
//
// Parent must mount this conditionally (only when open) so useState re-seeds
// on each open — avoids a setState-in-effect pattern (same convention as
// AsiakasEditDialog).
export default function EntityFormDialog({ open, onClose, title, schema, row, onSubmit, entityTag, validateForm, children }) {
    const t = useT();
    const [form, setForm] = useState(() => buildInitialFromSchema(schema, row || {}));
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle"); // idle | submitting | error
    const [submitError, setSubmitError] = useState(null);

    const update = useFieldSetter(setForm, errors, setErrors);

    async function handleSubmit(e) {
        e.preventDefault();
        const v = validateSchema(schema, form, t);
        setErrors(v);
        const formMsg = validateForm ? validateForm(form) : null;
        setSubmitError(formMsg);
        if (Object.keys(v).length || formMsg) return;
        setStatus("submitting");
        setSubmitError(null);
        try {
            await onSubmit(form);
            onClose();
        } catch (err) {
            captureError(err, { tags: { entity: entityTag, operation: row ? "edit" : "create" } });
            setSubmitError(err?.message || t("provider.entityFormDialog.saveFailed"));
            setStatus("error");
        }
    }

    // Render the raw control for one field (no key — the row wrapper carries it).
    // Switches are handled inline in the row builder so their help caption can
    // sit under the label. A field's `help` renders as helperText on solo
    // number/text fields; on a half-pair the shared caption is drawn under the
    // row instead (so min/max don't each repeat the same line).
    const renderControl = (f) => {
        if (f.type === "address") {
            return <AddressField field={f} form={form} setForm={setForm} errors={errors} update={update} />;
        }
        if (f.type === "number") {
            return (
                <TextField
                    label={f.label}
                    fullWidth
                    type="number"
                    value={form[f.key]}
                    onChange={update(f.key)}
                    error={!!errors[f.key]}
                    helperText={errors[f.key] || (f.half ? " " : f.help) || " "}
                    slotProps={f.min != null ? { htmlInput: { min: f.min } } : undefined}
                />
            );
        }
        return (
            <TextField
                label={f.label}
                fullWidth
                select={f.type === "select"}
                type={f.type === "email" ? "email" : "text"}
                multiline={!!f.multiline}
                minRows={f.multiline ? 2 : undefined}
                value={form[f.key]}
                onChange={update(f.key)}
                error={!!errors[f.key]}
                helperText={errors[f.key] || (f.half ? " " : f.help) || " "}
            >
                {f.type === "select" && (f.options || []).map((o) => (
                    <MenuItem key={o.value} value={String(o.value)}>
                        {o.label}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    // Build rows: two consecutive visible `half` fields share one row; the
    // first field's `help` becomes a single caption under that row.
    const visibleFields = schema.filter((f) => !f.visibleWhen || f.visibleWhen(form));
    const rows = [];
    for (let i = 0; i < visibleFields.length; i++) {
        const f = visibleFields[i];
        const next = visibleFields[i + 1];
        if (f.half && next && next.half) {
            rows.push(
                <Box key={f.key}>
                    <Stack direction="row" spacing={1.75}>
                        <Box sx={{ flex: 1 }}>{renderControl(f)}</Box>
                        <Box sx={{ flex: 1 }}>{renderControl(next)}</Box>
                    </Stack>
                    {f.help && (
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: -0.5 }}>
                            {f.help}
                        </Typography>
                    )}
                </Box>
            );
            i++; // consumed `next`
            continue;
        }
        if (f.type === "switch") {
            rows.push(
                <Box key={f.key}>
                    <FormControlLabel
                        label={f.label}
                        control={
                            <Switch
                                checked={!!form[f.key]}
                                onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.checked }))}
                            />
                        }
                    />
                    {f.help && (
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                            {f.help}
                        </Typography>
                    )}
                </Box>
            );
            continue;
        }
        rows.push(<Box key={f.key}>{renderControl(f)}</Box>);
    }

    return (
        <GlassDialog open={open} onClose={onClose} busy={status === "submitting"} title={title}>
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <Stack spacing={1.75}>
                    {rows}
                    {children}
                    {submitError && (
                        <Typography variant="caption" color="error" sx={{ display: "block" }}>
                            {submitError}
                        </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ pt: 1, justifyContent: "flex-end" }}>
                        <Button onClick={onClose} disabled={status === "submitting"}>
                            {t("provider.shared.cancel")}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={status === "submitting"}
                            startIcon={status === "submitting" ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {status === "submitting" ? t("provider.entityFormDialog.savingLabel") : t("provider.entityFormDialog.saveLabel")}
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        </GlassDialog>
    );
}
